
import { Component, Vue } from 'vue-property-decorator'
import Empty from '@/components/Empty/index.vue'
import ImageUpload from '@/components/ImgUpload/index.vue'
import {
  getStorePageByEmp,
  addStore,
  updateStore,
  deleteStore,
  getStoreInfo,
  auditStore,
  auditEnableStore,
  applyEnableStore,
  submitStoreAudit
} from '@/api/store'
import { getDishPage } from '@/api/dish'
import { UserModule } from '@/store/modules/user'
import { Form as ElForm } from 'element-ui'

const emptyForm = () => ({
  storeName: '',
  storePhone: '',
  provinceName: '',
  cityName: '',
  districtName: '',
  detail: '',
  latitude: '',
  longitude: '',
  logoUrl: '',
  status: 1,
  assignMode: 1,
  managerAcceptDelivery: 0,
  emptyBucketDepositDishId: null as number | null
})

@Component({
  name: 'StoreManage',
  components: {
    Empty,
    ImageUpload
  }
})
export default class extends Vue {
  private storeName: string = ''
  private status: number | null = null
  private auditStatus: number | null = null
  private counts: number = 0
  private page: number = 1
  private pageSize: number = 10
  private tableData: any[] = []
  private isSearch: boolean = false

  private dialogVisible: boolean = false
  private dialogTitle: string = '添加商户'
  private isEdit: boolean = false
  private currentId: string | number | null = null
  private submitLoading: boolean = false
  private editingAuditStatus: number | null = null

  private form: any = emptyForm()
  private depositDishOptions: any[] = []
  private depositDishLoading: boolean = false

  get isSuperAdmin() {
    return UserModule.isSuperAdmin
  }

  get isPlatformLevel() {
    return UserModule.isPlatformLevel
  }

  get isPlatformAdmin() {
    return UserModule.isPlatformAdmin
  }

  get canCreate() {
    // 商家管理员不可新建商户（仅超管/平台）
    if (UserModule.isStoreLocked) return false
    return UserModule.hasPermission('store:create')
  }

  get canUpdate() {
    return UserModule.hasPermission('store:update')
  }

  get canApplyEnablePerm() {
    return UserModule.hasPermission('store:applyEnable')
  }

  get canDelete() {
    return UserModule.hasPermission('store:delete')
  }

  get canAudit() {
    return UserModule.hasPermission('store:audit')
  }

  get canAuditEnable() {
    return UserModule.hasPermission('store:auditEnable')
  }

  get isApprovedEdit() {
    return this.editingAuditStatus == null || this.editingAuditStatus === 1
  }

  /** 列表中仍有未完成首次启用的商户时显示审核列 */
  get showAuditColumn() {
    const rows = this.tableData || []
    return rows.some((row) => !this.isFirstEnabledDone(row))
  }

  /** 入驻待审 */
  private isEntryPending(row: any) {
    return row && Number(row.auditStatus) === 0
  }

  /** 启用申请待审 */
  private isEnablePending(row: any) {
    return row && !this.isFirstEnabledDone(row) && Number(row.enableAuditStatus) === 1
  }

  private isApproved(row: any) {
    const a = row && row.auditStatus
    return a == null || Number(a) === 1
  }

  /** 已完成首次启用（存量 null 视为已完成） */
  private isFirstEnabledDone(row: any) {
    if (!row) return true
    const f = row.firstEnabled
    return f == null || Number(f) === 1
  }

  private enableAuditOf(row: any) {
    if (!row || row.enableAuditStatus == null) return 0
    return Number(row.enableAuditStatus)
  }

  /** 驳回/未提交：可再次提交入驻审核 */
  private canSubmitAudit(row: any) {
    if (!this.canUpdate || !row || this.isFirstEnabledDone(row)) return false
    const a = row.auditStatus == null ? 3 : Number(row.auditStatus)
    return a === 2 || a === 3
  }

  /** 入驻已通过，且启用未申请/已驳回 */
  private canApplyEnable(row: any) {
    if (!this.canApplyEnablePerm || !row || this.isFirstEnabledDone(row)) return false
    if (Number(row.auditStatus) !== 1) return false
    const e = this.enableAuditOf(row)
    return e === 0 || e === 3
  }

  private canEnter(row: any) {
    // 入驻审核通过即可进入配置；营业须启用审核通过
    return (
      UserModule.hasPermission('store:enter') &&
      this.isApproved(row) &&
      (this.isPlatformLevel || !UserModule.isStoreLocked)
    )
  }

  private auditLabel(row: any) {
    if (this.isFirstEnabledDone(row)) return '已启用'
    const e = this.enableAuditOf(row)
    if (e === 1) return '待启用审核'
    if (e === 3) return '启用已驳回'
    const s = row && row.auditStatus == null ? 1 : Number(row.auditStatus)
    if (s === 3) return '未提交审核'
    if (s === 0) return '待入驻审核'
    if (s === 2) return '入驻已驳回'
    if (s === 1) return '已通过·待申请启用'
    return '—'
  }

  private auditClass(row: any) {
    if (this.isFirstEnabledDone(row)) return 'audit-ok'
    const e = this.enableAuditOf(row)
    if (e === 1) return 'audit-pending'
    if (e === 3) return 'audit-rejected'
    const s = row && row.auditStatus == null ? 1 : Number(row.auditStatus)
    if (s === 3) return 'audit-not-applied'
    if (s === 0) return 'audit-pending'
    if (s === 2) return 'audit-rejected'
    return 'audit-ok'
  }

  private depositDishLabel(d: any) {
    if (!d) return ''
    const price = d.price != null ? Number(d.price) : NaN
    const priceText = Number.isFinite(price) ? `¥${price.toFixed(2)}` : ''
    const statusText = d.status === 0 ? '（停售）' : ''
    return [d.name, priceText, statusText].filter(Boolean).join(' ')
  }

  private async loadDepositDishOptions(storeId: string | number | null) {
    this.depositDishOptions = []
    if (storeId == null || storeId === '') return
    this.depositDishLoading = true
    try {
      const res: any = await getDishPage({
        page: 1,
        pageSize: 200,
        storeId
      })
      const records =
        (res && res.data && res.data.data && res.data.data.records) ||
        (res && res.data && res.data.records) ||
        []
      this.depositDishOptions = Array.isArray(records) ? records : []
    } catch (e) {
      this.depositDishOptions = []
    } finally {
      this.depositDishLoading = false
    }
  }

  get rules(): any {
    return {
      storeName: [{ required: true, message: '请输入商户名称', trigger: 'blur' }],
      storePhone: [
        { required: true, message: '请输入联系电话', trigger: 'blur' },
        { pattern: /^1\d{10}$/, message: '请输入11位手机号', trigger: 'blur' }
      ],
      provinceName: [{ required: true, message: '请填写省', trigger: 'blur' }],
      cityName: [{ required: true, message: '请填写市', trigger: 'blur' }],
      districtName: [{ required: true, message: '请填写区/县', trigger: 'blur' }],
      detail: [{ required: true, message: '请填写门牌地址', trigger: 'blur' }],
      longitude: [
        {
          validator: (_rule: any, value: any, callback: Function) => {
            const lngStr = value != null && value !== '' ? String(value).trim() : ''
            if (!lngStr) {
              callback(new Error('请填写经度'))
              return
            }
            if (!/^-?\d+(\.\d{1,18})?$/.test(lngStr)) {
              callback(new Error('经度格式不正确，小数最多 18 位'))
              return
            }
            const lng = Number(lngStr)
            if (!Number.isFinite(lng) || lng < -180 || lng > 180) {
              callback(new Error('经度需在 -180～180 之间'))
              return
            }
            callback()
          },
          trigger: ['blur', 'change']
        }
      ],
      latitude: [
        {
          validator: (_rule: any, value: any, callback: Function) => {
            const latStr = value != null && value !== '' ? String(value).trim() : ''
            if (!latStr) {
              callback(new Error('请填写纬度'))
              return
            }
            if (!/^-?\d+(\.\d{1,18})?$/.test(latStr)) {
              callback(new Error('纬度格式不正确，小数最多 18 位'))
              return
            }
            const lat = Number(latStr)
            if (!Number.isFinite(lat) || lat < -90 || lat > 90) {
              callback(new Error('纬度需在 -90～90 之间'))
              return
            }
            callback()
          },
          trigger: ['blur', 'change']
        }
      ],
      status: [{ required: true, message: '请选择状态', trigger: 'change' }]
    }
  }

  created() {
    this.init()
  }

  private displayAddress(row: any) {
    if (row.address && String(row.address).trim()) {
      return row.address
    }
    const parts = [row.provinceName, row.cityName, row.districtName, row.detail]
      .filter((p) => p && String(p).trim() && p !== '待完善')
    return parts.length ? parts.join('') : '—'
  }

  private onLogoChange(url: string) {
    this.form.logoUrl = url || ''
  }

  private initSearch() {
    this.page = 1
    this.init(true)
  }

  private async init(isSearch?: boolean) {
    this.isSearch = !!isSearch
    const params: any = {
      page: this.page,
      pageSize: this.pageSize
    }
    if (this.storeName) {
      params.storeName = this.storeName
    }
    if (this.status !== null && this.status !== undefined) {
      params.status = this.status
    }
    if (this.auditStatus !== null && this.auditStatus !== undefined) {
      params.auditStatus = this.auditStatus
    }
    try {
      const res: any = await getStorePageByEmp(params)
      if (String(res.data.code) === '1') {
        const data = res.data.data
        this.tableData = (data && data.records) || data || []
        this.counts = (data && data.total) || this.tableData.length
      } else {
        this.$message.error(res.data.msg || '获取商户列表失败')
      }
    } catch (err) {
      this.$message.error('请求出错了：' + (err as Error).message)
    }
  }

  private handleSizeChange(val: number) {
    this.pageSize = val
    this.init()
  }

  private handleCurrentChange(val: number) {
    this.page = val
    this.init()
  }

  /** 仅有 address 时尝试拆成省市区+门牌，便于编辑旧数据 */
  private parseAddressParts(addr: string) {
    const s = (addr || '').trim()
    if (!s) return null
    const m = s.match(
      /^(.+?(?:省|自治区|特别行政区))(.+?(?:市|自治州|地区|盟))?(.+?(?:区|县|旗))?([\s\S]*)$/
    )
    if (!m) return { detail: s }
    return {
      provinceName: (m[1] || '').trim(),
      cityName: (m[2] || '').trim(),
      districtName: (m[3] || '').trim(),
      detail: (m[4] || '').trim()
    }
  }

  private fillFormFromRow(row: any) {
    const norm = (v: any) => (v === '待完善' ? '' : (v || ''))
    let provinceName = norm(row.provinceName)
    let cityName = norm(row.cityName)
    let districtName = norm(row.districtName)
    let detail = norm(row.detail)
    const fullAddr = norm(row.address)

    if (!provinceName && !cityName && !districtName && !detail && fullAddr) {
      const parsed = this.parseAddressParts(fullAddr)
      if (parsed) {
        provinceName = parsed.provinceName || provinceName
        cityName = parsed.cityName || cityName
        districtName = parsed.districtName || districtName
        detail = parsed.detail || detail
      }
    }

    this.form = {
      storeName: row.storeName || '',
      storePhone: row.storePhone || '',
      provinceName,
      cityName,
      districtName,
      detail,
      latitude: row.latitude != null ? String(row.latitude) : '',
      longitude: row.longitude != null ? String(row.longitude) : '',
      logoUrl: row.logoUrl || '',
      status: row.status != null ? row.status : 1,
      assignMode: row.assignMode != null ? Number(row.assignMode) : 1,
      managerAcceptDelivery: row.managerAcceptDelivery != null ? Number(row.managerAcceptDelivery) : 0,
      emptyBucketDepositDishId:
        row.emptyBucketDepositDishId != null ? Number(row.emptyBucketDepositDishId) : null
    }
  }

  private async openEditDialog(type: 'add' | 'edit', row?: any) {
    if (type === 'add') {
      this.dialogTitle = this.isSuperAdmin ? '添加商户' : '申请新建商户'
      this.isEdit = false
      this.currentId = null
      this.editingAuditStatus = null
      this.form = emptyForm()
      // 平台新建默认停业；入驻审核需用户自行「提交审核」
      if (!this.isSuperAdmin) {
        this.form.status = 0
      }
      this.depositDishOptions = []
    } else {
      this.dialogTitle = '编辑商户'
      this.isEdit = true
      this.currentId = row.id
      this.editingAuditStatus =
        row.auditStatus != null ? Number(row.auditStatus) : 1
      this.fillFormFromRow(row)
      try {
        const res: any = await getStoreInfo(row.id)
        if (String(res.data.code) === '1' && res.data.data) {
          this.fillFormFromRow(res.data.data)
          this.editingAuditStatus =
            res.data.data.auditStatus != null
              ? Number(res.data.data.auditStatus)
              : 1
        }
      } catch (e) {
        // 列表数据已填充，忽略详情拉取失败
      }
      await this.loadDepositDishOptions(row.id)
    }
    this.dialogVisible = true
    this.$nextTick(() => {
      const ref = this.$refs.formRef as ElForm
      if (ref) ref.clearValidate()
    })
  }

  private onDialogClosed() {
    this.form = emptyForm()
    this.submitLoading = false
  }

  private buildPayload() {
    const f = this.form
    const province = (f.provinceName || '').trim()
    const city = (f.cityName || '').trim()
    const district = (f.districtName || '').trim()
    const detail = (f.detail || '').trim()
    const address = [province, city, district, detail].filter(Boolean).join('')

    const latStr = (f.latitude != null ? String(f.latitude) : '').trim()
    const lngStr = (f.longitude != null ? String(f.longitude) : '').trim()
    if (!latStr || !lngStr) {
      throw new Error('请填写纬度和经度')
    }
    if (!/^-?\d+(\.\d{1,18})?$/.test(latStr)) {
      throw new Error('纬度格式不正确，小数最多 18 位')
    }
    if (!/^-?\d+(\.\d{1,18})?$/.test(lngStr)) {
      throw new Error('经度格式不正确，小数最多 18 位')
    }
    const latitude = Number(latStr)
    const longitude = Number(lngStr)
    if (isNaN(latitude) || latitude < -90 || latitude > 90) {
      throw new Error('纬度需在 -90～90 之间')
    }
    if (isNaN(longitude) || longitude < -180 || longitude > 180) {
      throw new Error('经度需在 -180～180 之间')
    }

    return {
      id: this.isEdit ? this.currentId : undefined,
      storeName: (f.storeName || '').trim(),
      storePhone: (f.storePhone || '').trim(),
      provinceName: province || undefined,
      cityName: city || undefined,
      districtName: district || undefined,
      detail: detail || undefined,
      address: address || undefined,
      // 以字符串提交，后端按 BigDecimal 精确解析，避免浮点截断
      latitude: latStr,
      longitude: lngStr,
      logoUrl: (f.logoUrl || '').trim() || undefined,
      status: f.status,
      assignMode: f.assignMode != null ? Number(f.assignMode) : 1,
      managerAcceptDelivery: f.managerAcceptDelivery != null ? Number(f.managerAcceptDelivery) : 0,
      emptyBucketDepositDishId:
        f.emptyBucketDepositDishId != null && f.emptyBucketDepositDishId !== ''
          ? Number(f.emptyBucketDepositDishId)
          : null
    }
  }

  private submitForm() {
    (this.$refs.formRef as ElForm).validate(async (valid: boolean) => {
      if (!valid) return
      this.submitLoading = true
      let payload: any
      try {
        payload = this.buildPayload()
      } catch (e) {
        this.submitLoading = false
        this.$message.warning((e as Error).message)
        return
      }
      try {
        const api = this.isEdit ? updateStore : addStore
        const res: any = await api(payload)
        if (String(res.data.code) === '1') {
          const okMsg = this.isEdit
            ? '编辑成功'
            : this.isSuperAdmin
              ? '添加成功'
              : '商户已创建，请完善信息后点击「提交审核」'
          this.$message.success(okMsg)
          this.dialogVisible = false
          window.dispatchEvent(new Event('shop-status-changed'))
          this.init()
        } else {
          this.$message.error(res.data.msg || '操作失败')
        }
      } catch (err) {
        this.$message.error('请求出错了：' + (err as Error).message)
      } finally {
        this.submitLoading = false
      }
    })
  }

  private submitAuditHandle(row: any) {
    this.$confirm(
      `确认提交商户「${row.storeName || ''}」的入驻审核？`,
      '提交审核',
      { type: 'warning' }
    )
      .then(async () => {
        try {
          const res: any = await submitStoreAudit(row.id)
          if (String(res.data.code) === '1') {
            this.$message.success('已提交审核')
            this.init()
          } else {
            this.$message.error(res.data.msg || '提交失败')
          }
        } catch (err) {
          this.$message.error('请求出错了：' + (err as Error).message)
        }
      })
      .catch(() => {})
  }

  private applyEnableHandle(row: any) {
    this.$confirm(
      `确认申请启用商户「${row.storeName || ''}」？提交后需管理员审核通过才会营业。`,
      '申请启用',
      { type: 'warning' }
    )
      .then(async () => {
        try {
          const res: any = await applyEnableStore(row.id)
          if (String(res.data.code) === '1') {
            this.$message.success('已提交启用申请，请等待管理员审核')
            this.init()
          } else {
            this.$message.error(res.data.msg || '申请失败')
          }
        } catch (err) {
          this.$message.error('请求出错了：' + (err as Error).message)
        }
      })
      .catch(() => {})
  }

  private auditHandle(row: any, auditStatus: number) {
    const pass = auditStatus === 1
    const title = pass
      ? '确认通过该商户入驻审核？通过后需再申请启用并由管理员审启用。'
      : '确认驳回该商户入驻申请？'
    this.$prompt(pass ? '可选填写备注' : '请填写驳回原因', title, {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      inputPlaceholder: pass ? '备注（可空）' : '驳回原因',
      inputValidator: (val: string) => {
        if (!pass && !(val && String(val).trim())) {
          return '请填写驳回原因'
        }
        return true
      }
    })
      .then(async ({ value }: any) => {
        try {
          const res: any = await auditStore({
            id: row.id,
            auditStatus,
            auditRemark: value != null ? String(value).trim() : ''
          })
          if (String(res.data.code) === '1') {
            this.$message.success(
              pass ? '入驻已通过，请再申请启用并等待管理员审核' : '已驳回'
            )
            this.init()
          } else {
            this.$message.error(res.data.msg || '审核失败')
          }
        } catch (err) {
          this.$message.error('请求出错了：' + (err as Error).message)
        }
      })
      .catch(() => {})
  }

  private auditEnableHandle(row: any, auditStatus: number) {
    const pass = auditStatus === 1
    const title = pass
      ? '确认通过该商户的启用申请？通过后将变为营业中。'
      : '确认驳回该商户的启用申请？'
    this.$prompt(pass ? '可选填写备注' : '请填写驳回原因', title, {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      inputPlaceholder: pass ? '备注（可空）' : '驳回原因',
      inputValidator: (val: string) => {
        if (!pass && !(val && String(val).trim())) {
          return '请填写驳回原因'
        }
        return true
      }
    })
      .then(async ({ value }: any) => {
        try {
          const res: any = await auditEnableStore({
            id: row.id,
            auditStatus,
            auditRemark: value != null ? String(value).trim() : ''
          })
          if (String(res.data.code) === '1') {
            this.$message.success(pass ? '已通过启用，商户现为营业中' : '已驳回启用申请')
            this.init()
          } else {
            this.$message.error(res.data.msg || '审核失败')
          }
        } catch (err) {
          this.$message.error('请求出错了：' + (err as Error).message)
        }
      })
      .catch(() => {})
  }

  private deleteStoreHandle(row: any) {
    this.$confirm('确认删除该商户？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
      .then(async () => {
        try {
          const res: any = await deleteStore(row.id)
          if (String(res.data.code) === '1') {
            this.$message.success('删除成功')
            this.init()
          } else {
            this.$message.error(res.data.msg || '删除失败')
          }
        } catch (err) {
          this.$message.error('请求出错了：' + (err as Error).message)
        }
      })
      .catch(() => {})
  }

  private async selectStore(row: any) {
    if (!this.isApproved(row)) {
      this.$message.warning('商户尚未通过入驻审核，暂不可进入')
      return
    }
    await UserModule.SetStore(String(row.id))
    this.$message.success(`已切换到商户：${row.storeName}`)
    this.$router.push('/dashboard')
  }
}
